<template>
  <div class="desempenho-atendentes-page da-premium">
    <div class="da-premium__inner">
      <HeroDesempenhoAtendentes
        :period-label="periodoTriggerLabel"
        :period-range="periodoTriggerRange"
      />

      <FiltrosDesempenhoAtendentes
        :show-store-select="facebookAccountsLista.length > 1"
        :store-options="storeSelectOptions"
        :chat-selecionado.sync="chatSelecionado"
        :periodo-inicio.sync="periodoInicio"
        :periodo-fim.sync="periodoFim"
        :atalho-dias="atalhoDias"
        :considerar-horario.sync="considerarHorarioAtendimento"
        :tem-horario="temHorarioConfigurado"
        :texto-horario="textoHorarioConfigurado"
        :carregando="carregando"
        @preset="onPresetPeriodo"
        @personalizado="selecionarPersonalizado"
        @aplicar="aplicarPeriodoEBuscar"
        @change-loja="agendarCarregarDados"
      />

      <KpisResumoAtendentes
        :cards="kpiCards"
        :finalizados-detalhe="kpiFinalizadosDetalhe"
        :loading="carregando"
      />

      <div class="da-premium__split">
        <div class="da-premium__main-col">
          <RankingAtendentes
            :rows="rankingRows"
            :loading="carregando"
            empty-text="Nenhum dado no período selecionado."
          />
        </div>
        <div class="da-premium__side-col">
          <InsightsDesempenho
            :bullets="insightsBullets"
            :leitura="insightsLeitura"
            :distribuicao="insightsDistribuicao"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  buscaResolvidos,
  buscaAtendentes,
  buscaChatsTeste,
  getAttendings,
} from '../../functions/basics/chat'
import {
  buildTabelaAtendentesRows,
  summarizeKpisFromRows,
  formatDeltaPct,
  kpiResumoContagens,
  computeTempoMedioResolucaoSomenteResolvidos,
} from './desempenho-atendentes/desempenho-atendentes-metrics'
import {
  filterResolvidosInicioNaFaixaBr,
  isoDatePickerToBrazilISO,
  addDaysBrazilISO,
  daysBetweenISOBr,
  brazilTodayISO,
  countComInicioNoIntervalo,
} from './operacao-central/operacao-central-utils'
import HeroDesempenhoAtendentes from './desempenho-atendentes/HeroDesempenhoAtendentes.vue'
import FiltrosDesempenhoAtendentes from './desempenho-atendentes/FiltrosDesempenhoAtendentes.vue'
import KpisResumoAtendentes from './desempenho-atendentes/KpisResumoAtendentes.vue'
import RankingAtendentes from './desempenho-atendentes/RankingAtendentes.vue'
import InsightsDesempenho from './desempenho-atendentes/InsightsDesempenho.vue'

/** Data local ao meio-dia (evita DST / bordas ao somar dias). */
function ymdToLocalNoon(ymd) {
  if (!ymd || typeof ymd !== 'string') return null
  const s = ymd.trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
  const [y, mo, d] = s.split('-').map(Number)
  return new Date(y, mo - 1, d, 12, 0, 0, 0)
}

/**
 * Valor do datepicker (string YYYY-MM-DD) ou Date → YYYY-MM-DD no calendário local.
 * Importante: new Date('2026-03-01') é UTC e vira dia anterior no BR — não usar assim.
 */
function periodoCampoParaYMD(val) {
  if (val == null || val === '') return ''
  if (typeof val === 'string') {
    const s = val.trim().slice(0, 10)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  }
  const x = val instanceof Date ? val : new Date(val)
  if (Number.isNaN(x.getTime())) return ''
  const y = x.getFullYear()
  const m = String(x.getMonth() + 1).padStart(2, '0')
  const day = String(x.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function iniciaisNome(nome) {
  if (!nome || typeof nome !== 'string') return '?'
  const p = nome.trim().split(/\s+/).filter(Boolean)
  if (!p.length) return '?'
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase()
  return (p[0][0] + p[p.length - 1][0]).toUpperCase()
}

function pillPrimeira(min, fmt) {
  if (min == null || Number.isNaN(min)) return { cls: 'da-rank__pill--na', lbl: '—' }
  const lbl = fmt(min)
  if (min < 12) return { cls: 'da-rank__pill--ok', lbl }
  if (min < 60) return { cls: 'da-rank__pill--med', lbl }
  return { cls: 'da-rank__pill--bad', lbl }
}

function pillResolucao(min, fmt) {
  if (min == null || Number.isNaN(min)) return { cls: 'da-rank__pill--na', lbl: '—' }
  const lbl = fmt(min)
  if (min < 120) return { cls: 'da-rank__pill--ok', lbl }
  if (min < 480) return { cls: 'da-rank__pill--med', lbl }
  return { cls: 'da-rank__pill--bad', lbl }
}

/** Coluna "Iniciadas" do ranking: regra explícita sobre `resolvidos` (somente esta métrica na UI). */
function contarIniciadasRankingPorAtendente(resolvidos, idLinha) {
  if (idLinha === 'bot' || idLinha === 'sem_atendente') return 0
  const idAtend = Number(idLinha)
  if (Number.isNaN(idAtend)) return 0
  const list = Array.isArray(resolvidos) ? resolvidos : []
  return list.filter(item => (
    item &&
    item.atendimento_bot === false &&
    item.fim_bot === null &&
    item.atendente_ativo &&
    Number(item.atendente_ativo.id) === idAtend
  )).length
}

/** Percentual para exibição na Distribuição (uma casa decimal, pt-BR). */
function formatPctDistribuicao(val) {
  if (val == null || Number.isNaN(val)) return '0,0%'
  const n = Math.min(100, Math.max(0, Number(val)))
  return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

export default {
  name: 'DesempenhoAtendentes',
  components: {
    HeroDesempenhoAtendentes,
    FiltrosDesempenhoAtendentes,
    KpisResumoAtendentes,
    RankingAtendentes,
    InsightsDesempenho,
  },
  data() {
    const fimBr = brazilTodayISO()
    const inicioBr = addDaysBrazilISO(fimBr, -6)
    return {
      facebookAccountsLista: [],
      chatSelecionado: null,
      carregando: false,
      periodoInicio: inicioBr,
      periodoFim: fimBr,
      atalhoDias: 7,
      considerarHorarioAtendimento: false,
      resolvidos: [],
      resolvidosAnterior: [],
      atendimentosAbertos: [],
      mapaAtendentes: {},
      _carregarTimer: null,
    }
  },
  computed: {
    storeSelectOptions() {
      return (this.facebookAccountsLista || []).map(conta => ({
        value: conta.chat_token,
        text: conta.name,
      }))
    },
    periodoTriggerLabel() {
      if (this.atalhoDias === 7) return 'Últimos 7 dias'
      if (this.atalhoDias === 15) return 'Últimos 15 dias'
      if (this.atalhoDias === 31) return 'Últimos 31 dias'
      return 'Período personalizado'
    },
    periodoTriggerRange() {
      const yi = periodoCampoParaYMD(this.periodoInicio)
      const yf = periodoCampoParaYMD(this.periodoFim)
      const i = yi ? ymdToLocalNoon(yi) : null
      const f = yf ? ymdToLocalNoon(yf) : null
      if (!i || !f) return ''
      const fmt = d => d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${fmt(i)} – ${fmt(f)}`
    },
    horarioSelecionado() {
      const chat = this.facebookAccountsLista.find(c => c.chat_token === this.chatSelecionado)
      return chat?.horario_funcionamento || []
    },
    temHorarioConfigurado() {
      return this.horarioSelecionado.length > 0
    },
    textoHorarioConfigurado() {
      const h = this.horarioSelecionado
      if (!h.length) return ''
      const nomesDia = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
      const porDia = {}
      h.forEach(({ dia, abre, fecha }) => {
        if (!porDia[dia]) porDia[dia] = []
        porDia[dia].push(`${abre || ''}-${fecha || ''}`)
      })
      return Object.keys(porDia)
        .sort((a, b) => Number(a) - Number(b))
        .map(d => `${nomesDia[Number(d)]}: ${porDia[d].join(', ')}`)
        .join(' | ')
    },
    periodoInicioISOBr() {
      return isoDatePickerToBrazilISO(this.periodoInicio)
    },
    periodoFimISOBr() {
      return isoDatePickerToBrazilISO(this.periodoFim)
    },
    periodoAnteriorBoundsBr() {
      const a = this.periodoInicioISOBr
      const b = this.periodoFimISOBr
      if (!a || !b) return { prevInicio: '', prevFim: '' }
      const span = Math.max(1, daysBetweenISOBr(a, b))
      return {
        prevFim: addDaysBrazilISO(a, -1),
        prevInicio: addDaysBrazilISO(a, -span),
      }
    },
    resolvidosFiltradosBr() {
      return filterResolvidosInicioNaFaixaBr(
        this.resolvidos,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    resolvidosAnteriorFiltradosBr() {
      const { prevInicio, prevFim } = this.periodoAnteriorBoundsBr
      return filterResolvidosInicioNaFaixaBr(this.resolvidosAnterior, prevInicio, prevFim)
    },
    tabelaAtendentesRaw() {
      return buildTabelaAtendentesRows(
        this.resolvidosFiltradosBr,
        this.mapaAtendentes,
        this.horarioSelecionado,
        this.considerarHorarioAtendimento,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
        this.resolvidos,
        this.atendimentosAbertos,
      )
    },
    /** Mesma regra de métricas que o resumo: lista completa `resolvidos` do período (API). */
    tabelaRankingRaw() {
      return buildTabelaAtendentesRows(
        this.resolvidos,
        this.mapaAtendentes,
        this.horarioSelecionado,
        this.considerarHorarioAtendimento,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
        this.resolvidos,
        this.atendimentosAbertos,
      )
    },
    tabelaAtendentesAnteriorRaw() {
      const { prevInicio, prevFim } = this.periodoAnteriorBoundsBr
      return buildTabelaAtendentesRows(
        this.resolvidosAnteriorFiltradosBr,
        this.mapaAtendentes,
        this.horarioSelecionado,
        this.considerarHorarioAtendimento,
        prevInicio,
        prevFim,
        this.resolvidosAnterior,
        null,
      )
    },
    totalComInicioNoPeriodoAtual() {
      return countComInicioNoIntervalo(
        this.atendimentosAbertos,
        this.resolvidos,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    totalComInicioNoPeriodoAnterior() {
      const { prevInicio, prevFim } = this.periodoAnteriorBoundsBr
      return countComInicioNoIntervalo([], this.resolvidosAnterior, prevInicio, prevFim)
    },
    kpisAtual() {
      return summarizeKpisFromRows(this.tabelaAtendentesRaw, this.totalComInicioNoPeriodoAtual)
    },
    kpisAnterior() {
      return summarizeKpisFromRows(this.tabelaAtendentesAnteriorRaw, this.totalComInicioNoPeriodoAnterior)
    },
    rankingOrdenado() {
      const rows = [...this.tabelaRankingRaw]
      const botIdx = rows.findIndex(r => r.id === 'bot')
      let bot = null
      if (botIdx >= 0) {
        bot = rows.splice(botIdx, 1)[0]
      }
      const rest = rows.sort((a, b) => b.numero_conversas - a.numero_conversas)
      return bot ? [bot, ...rest] : rest
    },
    rankingRows() {
      const list = this.rankingOrdenado
      const maxVol = Math.max(...list.map(r => r.numero_conversas), 1)
      const taxas = list.map(r => (
        r.numero_conversas ? (r.contagem_resolucao / r.numero_conversas) * 100 : 0
      ))
      const maxTaxa = Math.max(...taxas, 1)
      const fmt = this.formatarTempoMinutos
      return list.map((r, idx) => {
        const taxa = r.numero_conversas ? (r.contagem_resolucao / r.numero_conversas) * 100 : 0
        const taxaFmt = `${taxa.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
        const p = pillPrimeira(r.tempo_medio_primeira_resposta, fmt)
        const q = pillResolucao(r.tempo_medio_resolucao, fmt)
        return {
          rank: idx + 1,
          id: r.id,
          nome: r.nome,
          isBot: r.id === 'bot',
          initials: iniciaisNome(r.nome),
          numero_conversas: r.numero_conversas,
          conversas_iniciadas_pelo_atendente: contarIniciadasRankingPorAtendente(this.resolvidos, r.id),
          contagem_resolucao: r.contagem_resolucao,
          lblPrimeira: p.lbl,
          clsPrimeira: p.cls,
          lblResolucao: q.lbl,
          clsResolucao: q.cls,
          taxaResolucaoFmt: taxaFmt,
          pctVolume: (r.numero_conversas / maxVol) * 100,
          pctTaxa: (taxa / maxTaxa) * 100,
          detalheRoute: this.rotaDetalheAtendente(r),
        }
      })
    },
    kpiFinalizadosDetalhe() {
      const k = kpiResumoContagens(this.resolvidos)
      return {
        semAtendenteVinculado: k.semAtendenteVinculado,
        resolvidosPelaEquipe: k.resolvidosPelaEquipe,
        resolvidosPeloBot: k.resolvidosPeloBot,
      }
    },
    kpiCards() {
      const k = kpiResumoContagens(this.resolvidos)
      const kPrev = kpiResumoContagens(this.resolvidosAnterior)
      const a = this.kpisAtual
      const p = this.kpisAnterior
      const fmt = this.formatarTempoMinutos
      const fmtPct = v => (v != null && !Number.isNaN(v) ? `${Math.round(v)}%` : '0%')

      const tempoMedioResolucaoKpi = computeTempoMedioResolucaoSomenteResolvidos(
        this.resolvidos,
        this.horarioSelecionado,
        this.considerarHorarioAtendimento,
      )
      const tempoMedioResolucaoKpiPrev = computeTempoMedioResolucaoSomenteResolvidos(
        this.resolvidosAnterior,
        this.horarioSelecionado,
        this.considerarHorarioAtendimento,
      )

      const deltaVol = formatDeltaPct(k.totalConversas, kPrev.totalConversas)
      const deltaFin = formatDeltaPct(k.totalFinalizados, kPrev.totalFinalizados)
      const tempoPriDelta = a.tempoMedioPrimeira != null && p.tempoMedioPrimeira != null
        ? formatDeltaPct(a.tempoMedioPrimeira, p.tempoMedioPrimeira)
        : null
      const tempoResDelta = tempoMedioResolucaoKpi != null && tempoMedioResolucaoKpiPrev != null
        ? formatDeltaPct(tempoMedioResolucaoKpi, tempoMedioResolucaoKpiPrev)
        : null
      const tempoPriBetter = tempoPriDelta && a.tempoMedioPrimeira != null && p.tempoMedioPrimeira != null
        ? a.tempoMedioPrimeira < p.tempoMedioPrimeira
        : null
      const tempoResBetter = tempoResDelta && tempoMedioResolucaoKpi != null && tempoMedioResolucaoKpiPrev != null
        ? tempoMedioResolucaoKpi < tempoMedioResolucaoKpiPrev
        : null

      const deltaPctBot = formatDeltaPct(k.pctBot, kPrev.pctBot)
      const deltaPctEquipe = formatDeltaPct(k.pctEquipe, kPrev.pctEquipe)

      return [
        {
          id: 'total',
          rowTop: true,
          featured: true,
          icon: 'MessageSquareIcon',
          label: 'Total de conversas',
          value: String(k.totalConversas),
          subtitle: 'Início no período selecionado',
          delta: deltaVol,
          deltaPositive: k.totalConversas >= kPrev.totalConversas,
        },
        {
          id: 'finalizados',
          rowTop: true,
          featured: false,
          clickable: true,
          label: 'Total de finalizados',
          value: String(k.totalFinalizados),
          subtitle: 'Toque para expandir o detalhe',
          delta: deltaFin,
          deltaPositive: k.totalFinalizados >= kPrev.totalFinalizados,
        },
        {
          id: 'tpri',
          label: 'Tempo médio 1ª resposta',
          value: fmt(a.tempoMedioPrimeira),
          subtitle: 'Média ponderada no período',
          delta: tempoPriDelta,
          deltaPositive: tempoPriBetter,
        },
        {
          id: 'tres',
          label: 'Tempo médio de resolução',
          value: fmt(tempoMedioResolucaoKpi),
          subtitle: 'Do início ao encerramento',
          delta: tempoResDelta,
          deltaPositive: tempoResBetter,
        },
        {
          id: 'pctbot',
          label: '% resolvidos pelo bot',
          value: fmtPct(k.pctBot),
          subtitle: 'Sobre o total de finalizados',
          delta: deltaPctBot,
          deltaPositive: null,
        },
        {
          id: 'pctequipe',
          label: '% resolvidos pela equipe',
          value: fmtPct(k.pctEquipe),
          subtitle: 'Sobre o total de finalizados',
          delta: deltaPctEquipe,
          deltaPositive: null,
        },
      ]
    },
    insightsBullets() {
      const rows = this.tabelaRankingRaw.filter(r => r.id !== 'sem_atendente')
      const bot = rows.find(r => r.id === 'bot')
      const humans = rows.filter(r => r.id !== 'bot')
      const bullets = []
      if (bot && humans.length && bot.tempo_medio_resolucao != null) {
        const humVals = humans.filter(h => h.tempo_medio_resolucao != null).map(h => h.tempo_medio_resolucao)
        if (humVals.length) {
          const avgH = humVals.reduce((s, v) => s + v, 0) / humVals.length
          if (avgH > 0 && bot.tempo_medio_resolucao > 0) {
            const ratio = (avgH / bot.tempo_medio_resolucao).toFixed(1)
            bullets.push(`No período selecionado, o bot levou em média ${ratio}× menos tempo de resolução que a média da equipe.`)
          }
        }
      }
      if (humans.length) {
        const byRes = [...humans].filter(h => h.tempo_medio_resolucao != null)
          .sort((a, b) => a.tempo_medio_resolucao - b.tempo_medio_resolucao)[0]
        if (byRes) {
          bullets.push(`${byRes.nome} tem o menor tempo médio de resolução entre a equipe no intervalo filtrado.`)
        }
        const byIni = [...humans].sort((a, b) => b.conversas_iniciadas_pelo_atendente - a.conversas_iniciadas_pelo_atendente)[0]
        if (byIni && byIni.conversas_iniciadas_pelo_atendente > 0) {
          bullets.push(`${byIni.nome} iniciou mais atendimentos diretamente no período.`)
        }
        const byTaxa = [...humans].filter(h => h.numero_conversas > 0)
          .map(h => ({
            ...h,
            taxa: h.contagem_resolucao / h.numero_conversas,
          }))
          .sort((a, b) => b.taxa - a.taxa)[0]
        if (byTaxa && byTaxa.taxa > 0) {
          bullets.push(`${byTaxa.nome} tem a maior taxa de resolução entre a equipe neste período.`)
        }
      }
      return bullets.slice(0, 5)
    },
    insightsLeitura() {
      const k = kpiResumoContagens(this.resolvidos)
      const tc = k.totalConversas
      if (!tc) {
        return 'Não há conversas listadas no período selecionado para uma leitura agregada. Ajuste o intervalo ou a loja e aplique novamente.'
      }
      const tf = k.totalFinalizados
      const pctBot = Math.round(k.pctBot || 0)
      const pctEquipe = Math.round(k.pctEquipe || 0)
      const somaIni = this.tabelaRankingRaw
        .filter(r => r.id !== 'bot')
        .reduce((s, r) => s + (r.conversas_iniciadas_pelo_atendente || 0), 0)
      const pctIni = Math.min(100, Math.round((somaIni / tc) * 100))
      if (tf === 0) {
        return (
          `Foram listadas ${tc} conversas no período. Nenhuma está com status resolvido nos dados retornados; os percentuais de finalização pelo bot e pela equipe aparecem como 0%. `
          + `Aproximadamente ${pctIni}% das conversas foram iniciadas diretamente por atendentes. Métricas de tempo respeitam o filtro de horário quando ativado.`
        )
      }
      return (
        `Foram listadas ${tc} conversas no período; ${tf} com status resolvido. Entre as finalizadas, cerca de ${pctBot}% foram resolvidas pelo bot e ${pctEquipe}% pela equipe. `
        + `Aproximadamente ${pctIni}% das conversas foram iniciadas diretamente por atendentes. Métricas de tempo respeitam o filtro de horário quando ativado.`
      )
    },
    insightsDistribuicao() {
      const k = kpiResumoContagens(this.resolvidos)
      const tc = k.totalConversas
      const temFinal = k.totalFinalizados > 0
      const pctBot = temFinal ? Math.min(100, k.pctBot || 0) : 0
      const pctEquipe = temFinal ? Math.min(100, k.pctEquipe || 0) : 0
      const somaIni = this.tabelaRankingRaw
        .filter(r => r.id !== 'bot')
        .reduce((s, r) => s + (r.conversas_iniciadas_pelo_atendente || 0), 0)
      const pctIniciadas = tc ? Math.min(100, (somaIni / tc) * 100) : 0
      return {
        pctBot,
        pctEquipe,
        pctIniciadas,
        pctBotFmt: formatPctDistribuicao(pctBot),
        pctEquipeFmt: formatPctDistribuicao(pctEquipe),
        pctIniciadasFmt: formatPctDistribuicao(pctIniciadas),
      }
    },
  },
  watch: {
    periodoInicio() {
      this.atualizarAtalhoDiasPelasDatas()
    },
    periodoFim() {
      this.atualizarAtalhoDiasPelasDatas()
    },
    '$route.query'(query) {
      this.aplicarFiltrosDaQuery(query)
      if (query && (query.periodo_inicio || query.periodo_fim || query.uuid)) {
        this.agendarCarregarDados()
      }
    },
  },
  beforeDestroy() {
    if (this._carregarTimer) {
      clearTimeout(this._carregarTimer)
      this._carregarTimer = null
    }
  },
  mounted() {
    this.carregarLojas().then(() => {
      this.aplicarFiltrosDaQuery(this.$route.query)
      this.agendarCarregarDados()
    })
  },
  methods: {
    onPresetPeriodo(dias) {
      this.aplicarPeriodo(dias)
      this.agendarCarregarDados()
    },
    aplicarPeriodo(dias) {
      const fimBr = brazilTodayISO()
      const inicioBr = addDaysBrazilISO(fimBr, -(dias - 1))
      this.periodoInicio = inicioBr
      this.periodoFim = fimBr
      this.atalhoDias = dias
    },
    selecionarPersonalizado() {
      this.atalhoDias = null
    },
    atualizarAtalhoDiasPelasDatas() {
      const yi = periodoCampoParaYMD(this.periodoInicio)
      const yf = periodoCampoParaYMD(this.periodoFim)
      const i = yi ? ymdToLocalNoon(yi) : null
      const f = yf ? ymdToLocalNoon(yf) : null
      if (!i || !f) return
      const diffDias = Math.round((f - i) / (1000 * 60 * 60 * 24)) + 1
      if (diffDias === 7) this.atalhoDias = 7
      else if (diffDias === 15) this.atalhoDias = 15
      else if (diffDias === 31) this.atalhoDias = 31
      else this.atalhoDias = null
    },
    aplicarPeriodoEBuscar() {
      this.agendarCarregarDados()
    },
    /** Une chamadas duplicadas (ex.: mount + change-loja) num único fetch. */
    agendarCarregarDados() {
      if (this._carregarTimer) clearTimeout(this._carregarTimer)
      this._carregarTimer = setTimeout(() => {
        this._carregarTimer = null
        this.executarCarregarDados()
      }, 150)
    },
    aplicarFiltrosDaQuery(query) {
      if (!query) return
      if (query.uuid && this.facebookAccountsLista.some(c => c.chat_token === query.uuid)) {
        this.chatSelecionado = query.uuid
      }
      if (query.periodo_inicio) {
        const s = String(query.periodo_inicio).slice(0, 10)
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) this.periodoInicio = s
      }
      if (query.periodo_fim) {
        const s = String(query.periodo_fim).slice(0, 10)
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) this.periodoFim = s
      }
      if (query.considerar_horario === '1' || query.considerar_horario === 'true') {
        this.considerarHorarioAtendimento = true
      }
      this.atualizarAtalhoDiasPelasDatas()
    },
    formatarTempoMinutos(valor) {
      if (valor == null || Number.isNaN(valor)) return '—'
      const n = Math.round(valor)
      if (n < 60) return `${n} min`
      const h = Math.floor(n / 60)
      const m = n % 60
      return m ? `${h}h ${m}min` : `${h}h`
    },
    rotaDetalheAtendente(item) {
      const inicio = isoDatePickerToBrazilISO(this.periodoInicio)
      const fim = isoDatePickerToBrazilISO(this.periodoFim)
      return {
        name: 'chat-desempenho-atendente-detalhe',
        query: {
          uuid: this.chatSelecionado || undefined,
          atendente_id: item.id,
          nome: item.nome,
          periodo_inicio: inicio,
          periodo_fim: fim,
          considerar_horario: this.considerarHorarioAtendimento ? '1' : undefined,
        },
      }
    },
    async carregarLojas() {
      const contas = await buscaChatsTeste()
      this.facebookAccountsLista = contas.chats || []
      if ((contas.chats || []).length === 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else if ((contas.chats || []).length > 1) {
        this.chatSelecionado = (contas.chats[0] && contas.chats[0].chat_token) || null
      } else {
        this.$bvToast.toast('Nenhuma loja encontrada.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async executarCarregarDados() {
      if (!this.chatSelecionado) return
      const inicioISO = isoDatePickerToBrazilISO(this.periodoInicio)
      const fimISO = isoDatePickerToBrazilISO(this.periodoFim)
      if (!inicioISO || !fimISO) {
        this.$bvToast.toast('Selecione data de início e fim do período.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }
      if (inicioISO > fimISO) {
        this.$bvToast.toast('A data de início não pode ser maior que a data de fim.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const tIni = new Date(`${inicioISO}T12:00:00-03:00`).getTime()
      const tFim = new Date(`${fimISO}T12:00:00-03:00`).getTime()
      if (Number.isNaN(tIni) || Number.isNaN(tFim)) {
        this.$bvToast.toast('Datas do período inválidas.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }
      const umMesEmMs = 1000 * 60 * 60 * 24 * 31
      if ((tFim - tIni) > umMesEmMs) {
        this.$bvToast.toast('O período não pode exceder 31 dias.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const spanDays = Math.max(1, daysBetweenISOBr(inicioISO, fimISO))
      const prevFimISO = addDaysBrazilISO(inicioISO, -1)
      const prevInicioISO = addDaysBrazilISO(inicioISO, -spanDays)

      this.carregando = true
      const uuid = this.chatSelecionado

      try {
        const [listaAtendentes, dadosResolvidos, dadosAnterior, dadosAtuais] = await Promise.all([
          buscaAtendentes(uuid),
          buscaResolvidos(uuid, inicioISO, fimISO, 'all'),
          buscaResolvidos(uuid, prevInicioISO, prevFimISO, 'all'),
          getAttendings(uuid, {
            pagina: 0,
            limite: 5000,
            corteAntesDe: new Date().toISOString(),
          }),
        ])
        this.mapaAtendentes = {}
        ;(listaAtendentes.atendentes || []).forEach(a => {
          this.mapaAtendentes[a.id] = a.name
        })
        this.resolvidos = (dadosResolvidos?.resolvidos || [])
        this.resolvidosAnterior = (dadosAnterior?.resolvidos || [])
        const listaAbertos = Array.isArray(dadosAtuais) ? dadosAtuais : (dadosAtuais?.atendimentos || [])
        this.atendimentosAbertos = listaAbertos.filter(at => at && at.atendimento_id)
      } catch (err) {
        console.error('Erro ao carregar desempenho:', err)
        this.$bvToast.toast(err.message || 'Erro ao carregar dados.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.resolvidos = []
        this.resolvidosAnterior = []
        this.atendimentosAbertos = []
      } finally {
        this.carregando = false
      }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');

.da-premium {
  --mc-bg: #f6f3fa;
  --mc-surface: #ffffff;
  --mc-surface-soft: #fbf8fe;
  --mc-border: #e9dff5;
  --mc-text: #3c315b;
  --mc-text-soft: #6f6787;
  --mc-purple: #6f35d0;
}

.da-premium {
  min-height: 100%;
  background: var(--mc-bg);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--mc-text);
  padding-bottom: 2.5rem;
}

.da-premium__inner {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 1.25rem 1.25rem 0;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .da-premium__inner {
    padding: 1.5rem 1.75rem 0;
  }
}

.da-premium__split {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: start;
}

@media (min-width: 1200px) {
  .da-premium__split {
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 1.75rem;
  }
}
</style>

<style>
body:has(.desempenho-atendentes-page) .content-area-wrapper {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
  width: 100% !important;
}

body:has(.desempenho-atendentes-page) .app-content.content {
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-width: 100% !important;
}

body:has(.desempenho-atendentes-page) .content-wrapper,
body:has(.desempenho-atendentes-page) .content-body {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

body:has(.desempenho-atendentes-page) .content-right {
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;
}
</style>
